/**
 * Role checking utility functions for Admin and Super Admin access control.
 *
 * Responsibilities:
 * - Centralized role detection logic
 * - Consistent super admin email list (matches backend configuration)
 */

// Super admin emails - must match the backend configuration in SUPER_ADMIN_EMAILS env var
const SUPER_ADMIN_EMAILS = ['kohsa07@gmail.com'];

/**
 * Check if user is a Super Admin
 * Super Admin can manage all institutes, tenants, and has cross-tenant access
 * @param {Object} user - User object from Redux store
 * @returns {boolean}
 */
export const isSuperAdmin = (user) => {
  if (!user?.email) return false;
  return user.isSuperAdmin === true ||
         SUPER_ADMIN_EMAILS.includes(user.email.toLowerCase());
};

/**
 * Check if user is the app Owner.
 *
 * Owner is backend-authoritative — identified via the OWNER_EMAILS env
 * var on the server. The frontend populates `user.isOwner` by calling
 * GET /admin/access/me once per session (ProfileHeader kicks it off
 * automatically for super-admin users). We don't hardcode any email
 * here because the owner list is deploy-configuration, not source code.
 *
 * @param {Object} user - User object from Redux store
 * @returns {boolean}
 */
export const isOwner = (user) => {
  if (!user?.email) return false;
  return user.isOwner === true;
};

/**
 * Check if user is an Institute Admin
 * Institute Admin can manage teachers and users within their tenant
 * @param {Object} user - User object from Redux store
 * @returns {boolean}
 */
export const isInstituteAdmin = (user) => {
  return user?.role === 'admin';
};

/**
 * Check if user has any admin access (Super Admin or Institute Admin)
 * Used for showing admin navigation dropdown
 * @param {Object} user - User object from Redux store
 * @returns {boolean}
 */
export const hasAdminAccess = (user) => {
  return isSuperAdmin(user) || isInstituteAdmin(user);
};

/**
 * Get user's admin role label for display
 * @param {Object} user - User object from Redux store
 * @returns {string|null} - Role label or null if not an admin
 */
export const getAdminRoleLabel = (user) => {
  if (isSuperAdmin(user)) return 'Super Admin';
  if (isInstituteAdmin(user)) return 'Admin';
  return null;
};
